import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, TouchableOpacity, View } from "react-native";
import hostingsHelper from "../../../../API/Hostings/hostingsHelper";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../../../Router";
import AgentBuilderSidebar from "../../../Agents/Components/AgentBuilderSidebar";
import HostingsSettingsAI from "./HostingsSettingsAI";

const HASH_ROUTED_SECTIONS = ["profile", "credentials", "endpoint"];

const buildInitialValues = () => ({
  name: "",
  description: "",
  tokenSecret: "",
  endpoint: "",
  modelName: "",
  customModel: false,
  apiVersion: null,
  type: "ollama",
  baseModel: null,
});

const fromHostingRecord = (hosting) => ({ ...hosting });

const HostingBuilderScreenContainer = () => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const history = useHistory();
  const params = useParams();
  const workspaceId = params?.workspaceId;
  const hostingId = params?.hostingId;
  const isEditMode = !!hostingId && hostingId !== "new";
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const AppToasty = AppToast.useToast();
  const formRef = useRef();
  const scrollRef = useRef();
  const [showUnsavedChoice, setShowUnsavedChoice] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const unblockRef = useRef(null);
  const { getActiveWorkspace } = useWorkspaces();

  const [activeSection, setActiveSection] = useState("profile");
  const [builderCtx, setBuilderCtx] = useState(null);
  const builderCtxRef = useRef(null);
  // Merge each reported context update instead of replacing it, so the live
  // `contextType` reported by the form is never clobbered by the dirty-tracking
  // `{ isDirty, errorSections }` payload (and vice-versa).
  const mergeBuilderCtx = useCallback((patch) => {
    setBuilderCtx((prev) => ({ ...(prev || {}), ...(patch || {}) }));
  }, []);
  const [saveVersion, setSaveVersion] = useState(0);
  const [initialMapValues, setInitialMapValues] = useState(null);
  const [isHostingsLoading, setIsHostingsLoading] = useState(true);
  const createInitRef = useRef(false);
  const editInitRef = useRef(null);

  const selectedHosting = useMemo(
    () => (state?.hostings || []).find((u) => u?.id === hostingId),
    [state?.hostings, hostingId]
  );

  const loadHostings = useCallback(
    async (workspaceID) => {
      try {
        setIsHostingsLoading(true);
        const hostingsData = await hostingsHelper.getHostings(
          workspaceID,
          null
        );
        let hostings = hostingsData?.data?.HostingsByWorkspace?.items;
        hostings =
          hostings
            ?.map((u) => {
              if (u?._deleted !== true)
                return {
                  ...u,
                };
            })
            ?.filter((u) => u != null) || [];
        dispatch("SET_HOSTINGS", hostings || []);
      } catch (error) {
        console.log(error);
        dispatch("SET_HOSTINGS", []);
      } finally {
        setIsHostingsLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    const hostings = state?.hostings || [];
    const belongsToWorkspace = hostings.some((u) => u?.workspaceID === wsId);
    if (hostings.length === 0 || !belongsToWorkspace) {
      loadHostings(wsId);
    } else {
      setIsHostingsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getActiveWorkspace()?.id]);

  useEffect(() => {
    if (!hostingId) {
      history.replace(`/workspaces/${workspaceId}/settings/hostings`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostingId, workspaceId]);

  useEffect(() => {
    if (isEditMode) return;
    if (hostingId !== "new") return;
    if (createInitRef.current) return;
    createInitRef.current = true;
    setInitialMapValues(buildInitialValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostingId, isEditMode]);

  useEffect(() => {
    if (!isEditMode) return;
    if (isHostingsLoading) return;
    if (editInitRef.current === hostingId) return;
    const activeWorkspaceId = getActiveWorkspace()?.id;
    if (!activeWorkspaceId) return;
    const hosting = (state?.hostings || []).find((u) => u?.id === hostingId);
    if (!hosting) {
      const listIsStale = state?.hostings?.some(
        (u) => u?.workspaceID && u?.workspaceID !== activeWorkspaceId
      );
      if (listIsStale) return;
      history.replace(`/workspaces/${activeWorkspaceId}/settings/hostings`);
      return;
    }
    editInitRef.current = hostingId;
    setInitialMapValues(fromHostingRecord(hosting));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hostingId,
    isEditMode,
    isHostingsLoading,
    state?.hostings,
    getActiveWorkspace()?.id,
  ]);

  const isSectionVisible = (sectionId, liveType) => {
    // `liveType` is the CURRENT provider type reported by the form (the type
    // dropdown is a live field). Fall back to the baked-in initial type so the
    // first render still has a sensible value.
    const type = liveType ?? initialMapValues?.type;
    switch (sectionId) {
      case "credentials":
        return type != null && !["none", "local", "ollama"].includes(type);
      case "endpoint":
        return (
          type != null && ["azure_openai", "local", "ollama"].includes(type)
        );
      default:
        return true;
    }
  };

  const liveType = builderCtx?.contextType ?? initialMapValues?.type;

  const sectionGroups = useMemo(() => {
    const groups = [
      {
        id: "setup",
        label: t("hostings.builderGroupSetup", { defaultValue: "Set up" }),
        items: [
          {
            id: "profile",
            title: t("hostings.builderSectionProfile", {
              defaultValue: "Overview",
            }),
            icon: AppAssets.icons.persona,
          },
          {
            id: "credentials",
            title: t("hostings.builderSectionCredentials", {
              defaultValue: "Credentials",
            }),
            icon: AppAssets.icons.credentials,
          },
        ],
      },
      {
        id: "system",
        label: t("hostings.builderGroupSystem", { defaultValue: "System" }),
        items: [
          {
            id: "endpoint",
            title: t("hostings.builderSectionEndpoint", {
              defaultValue: "Endpoint",
            }),
            icon: AppAssets.icons.hosting,
          },
        ],
      },
    ];
    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter((item) => isSectionVisible(item.id, liveType)),
      }))
      .filter((g) => g.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveType, t]);

  useEffect(() => {
    if (!isSectionVisible(activeSection, liveType)) {
      setActiveSection("profile");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveType, activeSection]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const applyHash = () => {
      const raw = (window.location.hash || "")
        .replace(/^#/, "")
        .trim()
        .toLowerCase();
      if (!raw) return;
      if (
        HASH_ROUTED_SECTIONS.includes(raw) &&
        isSectionVisible(raw, liveType)
      ) {
        setActiveSection(raw);
      }
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostingId, isEditMode, liveType]);

  useEffect(() => {
    builderCtxRef.current = builderCtx;
  }, [builderCtx]);

  useEffect(() => {
    unblockRef.current = history.block((location) => {
      const ctxIsDirty = builderCtxRef.current?.isDirty;
      const formikDirty = !!formRef?.current?.dirty;
      const isDirty = ctxIsDirty ?? formikDirty;
      if (!isDirty) return true;
      const builderPrefix = `/workspaces/${workspaceId}/hostings/`;
      if (location?.pathname?.startsWith(builderPrefix)) return true;
      setPendingNavigation(location?.pathname);
      setShowUnsavedChoice(true);
      return false;
    });
    return () => {
      unblockRef.current?.();
      unblockRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e) => {
      const isDirty =
        builderCtxRef.current?.isDirty ?? !!formRef?.current?.dirty;
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const handleStayOnBuilder = () => {
    setShowUnsavedChoice(false);
    setPendingNavigation(null);
  };

  const handleLeaveBuilder = () => {
    setShowUnsavedChoice(false);
    const target = pendingNavigation;
    setPendingNavigation(null);
    unblockRef.current?.();
    unblockRef.current = null;
    if (target) history.push(target);
  };

  const errorSections = builderCtx?.errorSections || [];
  const errorSectionsKey = JSON.stringify(errorSections);
  const prevErrorSectionsKeyRef = useRef("[]");
  useEffect(() => {
    if (prevErrorSectionsKeyRef.current === errorSectionsKey) return;
    prevErrorSectionsKeyRef.current = errorSectionsKey;
    if (errorSections.length === 0) return;
    const sectionTitles = sectionGroups
      .flatMap((g) => g.items)
      .filter((i) => errorSections.includes(i.id))
      .map((i) => i.title);
    if (sectionTitles.length === 0) return;
    setActiveSection(errorSections[0]);
    AppToasty.show(
      t("hostings.builderValidationErrors", {
        defaultValue: "Some required fields need attention in: {{sections}}",
        sections: sectionTitles.join(", "),
      }),
      { type: "danger" }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorSectionsKey]);

  const lastHashTokenRef = useRef(null);
  useEffect(() => {
    const token = activeSection;
    if (!token || lastHashTokenRef.current === token) return;
    lastHashTokenRef.current = token;
    if (typeof window === "undefined") return;
    const currentHash = (window.location.hash || "")
      .replace(/^#/, "")
      .toLowerCase();
    if (currentHash === token) return;
    const { pathname, search } = window.location;
    window.history.replaceState(
      window.history.state || null,
      "",
      `${pathname}${search}#${token}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  const isFormReady =
    initialMapValues != null &&
    !isHostingsLoading &&
    (!isEditMode || initialMapValues?.id === hostingId);

  useEffect(() => {
    scrollRef.current?.scrollTo?.({ y: 0, animated: false });
  }, [activeSection]);

  const handleHostingCreated = (status, savedId, submittedValues) => {
    if (status === "success") {
      AppToasty.show(t("hostingCreationSuccessCaption"), {
        type: "success",
      });
      formRef?.current?.resetForm?.({
        values: submittedValues || formRef.current?.values,
      });
      setSaveVersion((v) => v + 1);
      if (!isEditMode && savedId) {
        createInitRef.current = false;
        history.replace(`/workspaces/${workspaceId}/hostings/${savedId}`);
      }
    } else {
      AppToasty.show(t("hostingCreationFailedCaption"), {
        type: "danger",
      });
    }
  };

  const renderSaveButton = () => (
    <ToothFairyButton
      title={t("common.save")}
      onPress={() => formRef?.current?.submitForm()}
      isDisabled={!isFormReady}
      wrapperStyle={{
        minWidth: 110,
        height: 36,
        marginTop: 10,
      }}
    />
  );

  const renderBody = () => {
    if (!isFormReady) {
      return (
        <View
          style={{ flex: 1, alignItems: "center", justifyContent: "center" }}
        >
          <ToothFairyLoadingPlaceholder
            caption={t("settings.list.loadingHostings")}
          />
        </View>
      );
    }
    return (
      <View style={{ flex: 1, flexDirection: "row", minHeight: 0 }}>
        <AgentBuilderSidebar
          groups={sectionGroups}
          activeSection={activeSection}
          onSelect={setActiveSection}
          errorSections={errorSections}
        />
        <ScrollView
          ref={scrollRef}
          style={{ flex: 1, minWidth: 0 }}
          contentContainerStyle={{ flexGrow: 1 }}
          showsVerticalScrollIndicator
        >
          <HostingsSettingsAI
            key={hostingId}
            ref={formRef}
            initialMapValues={initialMapValues}
            isEdit={isEditMode}
            selectedId={isEditMode ? hostingId : undefined}
            opt={isEditMode ? selectedHosting : undefined}
            settingType="hosting"
            onHostingCreated={handleHostingCreated}
            onHostingDeleted={() =>
              history.push(`/workspaces/${workspaceId}/settings/hostings`)
            }
            isBuilderLayout
            builderActiveSection={activeSection}
            onBuilderContextChange={mergeBuilderCtx}
            saveVersion={saveVersion}
          />
        </ScrollView>
      </View>
    );
  };

  return (
    <View
      style={{
        flex: 1,
        height: "100%",
        backgroundColor: currentTheme?.theme?.backgroundColor,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 16,
          paddingVertical: 10,
          borderBottomWidth: 1,
          borderBottomColor: currentTheme?.theme?.lightGrey,
        }}
      >
        <TouchableOpacity
          onPress={() =>
            history.push(`/workspaces/${workspaceId}/settings/hostings`)
          }
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginRight: 14,
            paddingVertical: 4,
            paddingHorizontal: 6,
          }}
        >
          <AppIcon
            icon={AppAssets.icons.arrow_left}
            size={13}
            color={currentTheme?.theme?.darkGrey}
          />
          <AppText
            style={{ marginLeft: 6, fontSize: 13 }}
            color={currentTheme?.theme?.darkGrey}
          >
            {t("hostings.builderBackLabel", { defaultValue: "Hostings" })}
          </AppText>
        </TouchableOpacity>
        <AppText
          style={{ fontSize: 15, flexShrink: 1 }}
          fontWeight="600"
          numberOfLines={1}
        >
          {isEditMode
            ? initialMapValues?.name || selectedHosting?.name || ""
            : t("hostings.builderNewHosting", {
                defaultValue: "New hosting",
              })}
        </AppText>
        <View style={{ flex: 1 }} />
        {renderSaveButton()}
      </View>
      {renderBody()}
      <ToothFairyChoice
        cancelCaption={t("settings.qa.unsavedChangesKeepEditing", {
          defaultValue: "Keep editing",
        })}
        caption={t("settings.qa.unsavedChangesCaption", {
          defaultValue: "Unsaved changes will be lost",
        })}
        subCaption={t("hostings.unsavedChangesSubCaption", {
          defaultValue:
            "This hosting has changes that haven't been saved. Leave now and they're gone for good.",
        })}
        confirmCaption={t("settings.qa.leaveWithoutSaving", {
          defaultValue: "Discard changes",
        })}
        onCancelButtonPress={handleStayOnBuilder}
        onConfirmButtonPress={handleLeaveBuilder}
        setShowChoice={setShowUnsavedChoice}
        showChoice={showUnsavedChoice}
      />
    </View>
  );
};

export default HostingBuilderScreenContainer;
